import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from 'components/core/theme-provider';
import type { Agent } from 'features/chat/agent-chat-module/types/agent.service.type';
import { cn } from 'lib/utils';
import { getPickerTheme } from 'pages/agents/utils/agent-config-picker-theme';
import { PreviewChatbot, parseChatbotWidgetData } from './preview-chatbot';

type AgentsConfigPreviewSectionProps = {
  agent?: Agent;
  projectKey: string;
  isSaving?: boolean;
  lastSaveTime?: Date | null;
};

export function AgentsConfigPreviewSection({
  agent,
  projectKey,
  isSaving = false,
  lastSaveTime = null,
}: AgentsConfigPreviewSectionProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const ui = getPickerTheme(theme === 'dark');
  const parsedWidget = agent?.widget ? parseChatbotWidgetData(agent.widget) : null;
  const widget = parsedWidget
    ? { ...parsedWidget, name: agent?.name?.trim() || parsedWidget.name }
    : null;
  const [chatbotKey, setChatbotKey] = useState(0);

  const resetChat = () => {
    setChatbotKey((current) => current + 1);
  };

  return (
    <div
      data-testid="agents-config-preview"
      className={cn(
        'flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-3xl border shadow-sm',
        ui.dialog,
      )}
    >
      <div className={cn('shrink-0 px-5 pb-3 pt-5', ui.header)}>
        <h2 className={cn('text-lg font-bold leading-tight tracking-tight', ui.title)}>
          {t('PREVIEW', { defaultValue: 'Preview' })}
        </h2>
      </div>
      <div className="flex min-h-0 flex-1 flex-col px-4 pb-4 pt-3">
        <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-2xl">
          {widget && agent?.widget_id && agent?.id ? (
            <PreviewChatbot
              key={chatbotKey}
              agentId={agent.id}
              widget={widget}
              widgetId={agent.widget_id}
              projectKey={projectKey}
              lastUpdatedDate={agent.last_updated_date}
              isSaving={isSaving}
              lastSaveTime={lastSaveTime}
              onResetChat={resetChat}
              variant="figma-edit"
              className="h-full w-full"
            />
          ) : (
            <div
              className={cn(
                'flex h-full min-h-0 w-full flex-col items-start overflow-hidden rounded-2xl border',
                ui.chipWrap,
              )}
            >
              <div className="flex w-full shrink-0 items-center gap-3 bg-black px-4 py-3 text-true-white dark:bg-[#171717]">
                <div
                  className="flex size-8 shrink-0 items-center justify-center rounded-full border border-white bg-white/15 text-xs font-semibold text-true-white"
                  data-testid="preview-chatbot-avatar"
                  aria-hidden
                >
                  {(agent?.name || t('AGENT', { defaultValue: 'Agent' })).trim().charAt(0).toUpperCase() || 'A'}
                </div>
                <span
                  className="min-w-0 flex-1 truncate text-sm font-semibold text-true-white"
                  title={agent?.name || t('AGENT', { defaultValue: 'Agent' })}
                >
                  {agent?.name || t('AGENT', { defaultValue: 'Agent' })}
                </span>
              </div>
              <div className={cn('flex min-h-0 w-full flex-1 items-center justify-center px-6 text-sm', ui.muted)}>
                {t('NO_PREVIEW_AVAILABLE', { defaultValue: 'No preview available' })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
